import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useGetDeployEnvVars } from "@/hooks/useDeploys";
import { Deploy, EnvironmentVariables } from "@/types";
import { Copy, Edit, Globe, Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { EditEnvVarsDialog } from "./EditEnvVarsDialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { reDeploy } from "@/lib/deploy/api";
import { FALLBACK_ERROR_MESSAGE } from "@/lib/constants";

interface SettingsTabProps {
  deploy: Deploy;
  onManageDomains: () => void;
  section?: "all" | "domains" | "environment-variables";
}

const panelClass =
  "rounded-2xl border border-slate-800/80 bg-slate-950/60 shadow-sm";
const panelHeaderClass =
  "flex flex-col gap-1 border-b border-slate-800/70 px-5 py-4";
const panelBodyClass = "px-5 py-4";

export function SettingsTab({
  deploy,
  onManageDomains,
  section = "all",
}: SettingsTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [envVariables, setEnvVariables] = useState<EnvironmentVariables[]>([]);
  const [shouldFetchEnvVars, setShouldFetchEnvVars] = useState(false);
  const envVariablesQuery = useGetDeployEnvVars(deploy.id, shouldFetchEnvVars);
  const [decrypted, setDecrypted] = useState<EnvironmentVariables[] | null>(
    null,
  );
  const [visibleKey, setVisibleKey] = useState<string | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [pendingEditOpen, setPendingEditOpen] = useState(false);

  const isDeleted = deploy.status === "deleted";

  const updateDeployMutation = useMutation({
    mutationFn: reDeploy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deploy", deploy.id] });
      queryClient.invalidateQueries({ queryKey: ["deploys"] });
      toast({
        title: "Deployment updated",
        description:
          "Environment variables updated successfully. A new deployment is in progress.",
      });
      setShowEditDialog(false);
    },
    onError: (error: any) => {
      console.error("Environment variables update failed.");
      toast({
        title: "Error",
        description: error?.response?.data?.message ?? FALLBACK_ERROR_MESSAGE,
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (deploy) {
      setEnvVariables(deploy.environmentVariables || []);
    }
  }, [deploy]);

  useEffect(() => {
    if (envVariablesQuery.data) {
      setDecrypted(envVariablesQuery.data);
    }
  }, [envVariablesQuery.data]);

  useEffect(() => {
    if (pendingEditOpen && decrypted) {
      setShowEditDialog(true);
      setPendingEditOpen(false);
    }
  }, [pendingEditOpen, decrypted]);

  const handleOpenEditDialog = () => {
    if (envVariables.length === 0) {
      setShowEditDialog(true);
      return;
    }
    if (!decrypted) {
      setShouldFetchEnvVars(true);
      setPendingEditOpen(true);
      return;
    }
    setShowEditDialog(true);
  };

  const handleToggle = (key: string) => {
    if (!decrypted) {
      setShouldFetchEnvVars(true);
      setVisibleKey(key);
      return;
    }
    setVisibleKey((prev) => (prev === key ? null : key));
  };

  const getValue = (key: string) => {
    if (!decrypted || visibleKey !== key) return "****";
    const match = decrypted.find((env) => env.key === key);
    return match?.value ?? "****";
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Value copied to clipboard.",
    });
  };

  const handleSaveEnvVars = async (newEnvVars: EnvironmentVariables[]) => {
    await updateDeployMutation.mutateAsync({
      id: deploy.id,
      environmentVariables: newEnvVars,
    });
  };

  const domainToCopy = deploy.customDomain || deploy.subDomain || "";
  const showDomains = section === "all" || section === "domains";
  const showEnvVars = section === "all" || section === "environment-variables";

  return (
    <>
      <div className="space-y-6">
        {showDomains && (
          <Card className={panelClass}>
            <CardHeader className={panelHeaderClass}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                    <Globe className="h-4 w-4 text-cyan-300" />
                    Domains
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">
                    Configure the URL used for this deployment.
                  </CardDescription>
                </div>
                <Button
                  onClick={onManageDomains}
                  variant="outline"
                  size="sm"
                  className="border-slate-800/80 bg-slate-950/60 text-slate-100 hover:bg-slate-900/70"
                  disabled={isDeleted}
                >
                  Manage domains
                </Button>
              </div>
            </CardHeader>
            <CardContent className={panelBodyClass}>
              <div className="rounded-xl border border-slate-800/70 bg-slate-950/60 px-4 py-3">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                  Current domain
                </p>
                <div className="mt-2 flex items-center gap-3">
                  <span className="min-w-0 flex-1 font-mono text-sm text-slate-100 break-all">
                    {domainToCopy || "No domain assigned"}
                  </span>
                  {domainToCopy && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyToClipboard(domainToCopy)}
                      className="text-slate-400 hover:text-slate-100"
                      disabled={isDeleted}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {showEnvVars && (
          <Card className={panelClass}>
            <CardHeader className={panelHeaderClass}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex-1">
                  <CardTitle className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                    <Lock className="h-4 w-4 text-cyan-300" />
                    Environment variables
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">
                    Secrets stay encrypted until you reveal them.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="border border-slate-700/70 bg-slate-900/70 text-slate-200">
                    {envVariables.length} vars
                  </Badge>
                  <Button
                    onClick={handleOpenEditDialog}
                    variant="outline"
                    size="sm"
                    className="border-slate-800/80 bg-slate-950/60 text-slate-100 hover:bg-slate-900/70"
                    disabled={isDeleted}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className={panelBodyClass}>
              {envVariables.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-800/70 bg-slate-950/50 p-10 text-center text-slate-400">
                  <Lock className="mx-auto mb-3 h-8 w-8 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-200">
                    No environment variables yet
                  </p>
                  <p className="text-xs text-slate-500">
                    Add secrets to configure this deployment.
                  </p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-slate-800/70">
                  <div className="grid grid-cols-12 gap-3 border-b border-slate-800/70 bg-slate-950/70 px-4 py-2 text-xs uppercase tracking-[0.18em] text-slate-500">
                    <span className="col-span-12 sm:col-span-5">Key</span>
                    <span className="col-span-12 sm:col-span-4">Value</span>
                    <span className="col-span-12 sm:col-span-3 sm:text-right">
                      Actions
                    </span>
                  </div>
                  <div className="divide-y divide-slate-800/70">
                    {envVariables.map((variable) => {
                      const value = getValue(variable.key);
                      const isRevealed = Boolean(
                        decrypted && visibleKey === variable.key,
                      );
                      const canCopy = isRevealed && value !== "****";

                      return (
                        <div
                          key={variable.key}
                          className="grid grid-cols-1 gap-3 px-4 py-3 sm:grid-cols-12 sm:items-center"
                        >
                          <div className="sm:col-span-5 min-w-0">
                            <p className="font-mono text-sm text-slate-100 truncate">
                              {variable.key}
                            </p>
                            <p className="text-xs text-slate-500">Hidden</p>
                          </div>
                          <div className="sm:col-span-4">
                            <span className="font-mono text-xs text-slate-300 break-all">
                              {isRevealed && value !== "****" ? value : "****"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 sm:col-span-3 sm:justify-end">
                            <Button
                              onClick={() => handleToggle(variable.key)}
                              variant="outline"
                              size="sm"
                              className="border-slate-800/80 bg-slate-950/60 text-slate-100 hover:bg-slate-900/70"
                              disabled={isDeleted}
                            >
                              {isRevealed ? "Hide" : "View"}
                            </Button>
                            {canCopy && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => copyToClipboard(value)}
                                className="text-slate-400 hover:text-slate-100"
                                aria-label={`Copy ${variable.key}`}
                                disabled={isDeleted}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      <EditEnvVarsDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        initialEnvVars={decrypted ?? envVariables}
        onSave={handleSaveEnvVars}
        isSaving={updateDeployMutation.isPending}
      />
    </>
  );
}
